/**
 * Route Metadata — Single Source of Truth
 *
 * Every public route on kaiyasolutions.com has one entry here. The <SEO />
 * component auto-resolves metadata from this file based on `useLocation()`,
 * which means the visible page content and the crawler-visible meta tags can
 * never drift apart again.
 *
 * Rules:
 * - Keys are exact route paths (no trailing slash, no query).
 * - Every entry MUST have: title, description.
 * - Optional: keywords, image, type, canonical (defaults derived below).
 * - Wildcard fallback lives in `defaultMetadata`.
 *
 * DO NOT hardcode meta tags per-page. If a route needs different SEO, add or
 * update its entry here.
 */

export const SITE_BASE_URL = 'https://kaiyasolutions.com';
const OG_IMAGE = 'https://customer-assets.emergentagent.com/job_af3c3704-fb13-4fba-b34c-30719b15ce33/artifacts/98r54tqy_Site%20Banner.png';

export const defaultMetadata = {
  title: 'Kaiya Solutions | Enterprise Compliance & Risk Infrastructure',
  description: 'AI-powered compliance and risk infrastructure for governments, tribal councils, nonprofits, and enterprises. GrantGuard, VendorGuard, PROOF, CouncilGuard, ManifestGuard on one unified evidence ledger.',
  keywords: 'compliance platform, risk infrastructure, audit defensibility, grant compliance, vendor management, contractor verification, governance records, customs compliance, evidence ledger',
  image: OG_IMAGE,
  type: 'website',
};

const KEYWORDS_COMPLIANCE = 'compliance software, audit preparation, evidence infrastructure';

// Keyed by exact pathname. Paths must match React Router routes.
export const routeMetadata = {
  // ============================================================
  // Homepage
  // ============================================================
  '/': {
    title: 'Kaiya Solutions | Enterprise Compliance & Risk Infrastructure',
    description: 'AI-powered compliance platform for governments, tribal councils, nonprofits, and enterprises. GrantGuard for federal grant compliance. VendorGuard for vendor due diligence. PROOF for contractor verification. CouncilGuard for governance records. ManifestGuard for customs compliance.',
    keywords: 'kaiya solutions, compliance platform, risk infrastructure, GrantGuard, VendorGuard, PROOF, CouncilGuard, ManifestGuard, audit defensibility, evidence ledger',
  },

  // ============================================================
  // Product Landings — Protection Modules
  // ============================================================
  '/grantguard': {
    title: 'GrantGuard | Federal Grant Compliance & Audit Preparation',
    description: 'Continuous 2 CFR 200 grant compliance monitoring for federal award recipients. Control mapping, gap detection, SEFA builder, subrecipient monitoring, mock audits, and audit-ready evidence packaging.',
    keywords: 'federal grant compliance, 2 CFR 200, single audit, SEFA, subrecipient monitoring, grant management software, audit preparation',
  },
  '/grantguard-tribal': {
    title: 'GrantGuard for Tribal Governments | 2 CFR 200 & NICRA',
    description: 'Grant compliance for sovereign nations and federally recognized tribes. NICRA management, council resolutions, cultural sensitivity, and tribal data sovereignty built in.',
    keywords: 'tribal grant compliance, NICRA, tribal government, 2 CFR 200, federal award, sovereign nation',
  },
  '/vendorguard': {
    title: 'VendorGuard | Vendor Due Diligence & COI Management',
    description: 'Automated vendor compliance monitoring, COI expiry tracking, W-9 collection, insurance verification, and risk scoring. Self-service vendor onboarding portal.',
    keywords: 'vendor compliance, COI tracking, vendor management, third-party risk, due diligence, insurance verification',
  },
  '/proof': {
    title: 'PROOF | Contractor Job Completion Verification',
    description: 'Tamper-proof job completion records with photo evidence, video capture, GPS, dual-party signatures, and shareable public links. Defense against chargebacks and disputes.',
    keywords: 'contractor verification, job completion, proof of service, dispute defense, dual signatures, evidence documentation',
  },
  '/proof/pricing': {
    title: 'PROOF Pricing | Free, Pro, and Enterprise Plans',
    description: 'PROOF Free forever, Pro at $49/mo, and Enterprise for teams. All tiers include photo evidence, dual signatures, and tamper-proof job completion records.',
    keywords: 'PROOF pricing, contractor verification pricing, job completion documentation cost',
  },
  '/proof/why-proof': {
    title: 'Why PROOF | The Case for Dual-Party Verification',
    description: 'Why job completion records need photo evidence, dual signatures, and tamper-proof timestamps. Prevents chargebacks, disputes, and lost revenue.',
    keywords: 'dual party verification, contractor disputes, job completion evidence',
  },
  '/proof/demo': {
    title: 'PROOF Demo | See Contractor Verification in Action',
    description: 'Walk through a live PROOF verification session. Photo upload, GPS capture, dual signatures, and shareable public link creation.',
    keywords: 'PROOF demo, contractor verification demo',
  },
  '/councilguard': {
    title: 'CouncilGuard | Governance Records for Councils & Boards',
    description: 'Governance record infrastructure for city councils, tribal councils, and nonprofit boards. Meetings, agendas, AI-generated minutes, decisions, motions/votes, and public transparency portal.',
    keywords: 'governance records, city council software, meeting minutes, motions and votes, transparency portal, board meeting software',
  },
  '/clerkassist': {
    title: 'ClerkAssist | AI Meeting Minutes for Councils',
    description: 'AI-powered meeting minutes generator. Upload audio or a video URL, get structured minutes with decisions, motions/votes, action items, and attendance. Integrates with CouncilGuard.',
    keywords: 'AI meeting minutes, council clerk software, meeting transcription, decision extraction, motion tracking',
  },
  '/manifestguard': {
    title: 'ManifestGuard | Import/Export & Customs Compliance',
    description: 'Pre-shipment document review, AI-assisted HS code classification, customs compliance checklists, and release control. Reduces delays and clearance risk.',
    keywords: 'customs compliance, import documentation, HS code classification, shipping compliance, manifest review',
  },
  '/manifestguard/terms': {
    title: 'ManifestGuard Terms | Kaiya Solutions',
    description: 'ManifestGuard-specific terms of service. Customs classification is AI-assisted and does not replace a licensed customs broker.',
    keywords: 'manifestguard terms',
  },
  '/ecom-protect': {
    title: 'ECom Protect | Autonomous Chargeback Defense & Revenue Recovery',
    description: 'Automated chargeback documentation, dispute representment, and revenue recovery for e-commerce brands. Shopify webhook integration, evidence packaging, and recovery email sequences.',
    keywords: 'chargeback defense, dispute representment, ecommerce chargebacks, revenue recovery, Shopify chargebacks',
  },
  '/contractorshield': {
    title: 'ContractorShield | Contractor Compliance Management',
    description: 'Structured compliance documentation for contractors — licensing, insurance, worker classification, and job completion evidence.',
    keywords: 'contractor compliance, contractor management, licensing tracking, worker classification',
  },
  '/risk-intelligence': {
    title: 'Risk Intelligence | Compliance & Operational Risk Content Library',
    description: 'AI-authored compliance and operational risk articles across grants, vendors, contractors, governance, customs, and chargebacks. Weekly signals for regulated operators.',
    keywords: 'risk intelligence, compliance content, operational risk, audit preparation guides, compliance insights',
  },
  '/risk-intelligence/products': {
    title: 'Risk Intelligence Reports & Products',
    description: 'Paid deep-dive reports on compliance, audit preparation, and operational risk. Practical guides for regulated operators.',
    keywords: 'compliance reports, audit preparation reports, risk intelligence products',
  },

  // ============================================================
  // Platform / Overview Pages
  // ============================================================
  '/kaiya-platform': {
    title: 'The Kaiya Platform | Modular Compliance Infrastructure',
    description: 'One unified authentication, one immutable audit trail, one evidence ledger — and eleven purpose-built compliance modules that share them all.',
    keywords: 'compliance platform, unified compliance software, evidence ledger, multi-module SaaS',
  },
  '/risk-infrastructure': {
    title: 'Risk Infrastructure | Kaiya Solutions',
    description: 'The control and accountability layer that regulated organizations lack. Documentation, verification, and evidence chains across every compliance domain.',
    keywords: 'risk infrastructure, control layer, accountability layer, compliance evidence',
  },
  '/protection-layers': {
    title: 'Protection Layers | Verification, Governance, Compliance',
    description: 'Three layered protection tiers: verification (PROOF), governance (CouncilGuard, ClerkAssist), and compliance (GrantGuard, VendorGuard, ManifestGuard, ECom Protect).',
    keywords: 'protection layers, compliance layers, operational protection, defense in depth',
  },
  '/evidence-ledger': {
    title: 'The Evidence Ledger | Platform-Wide Audit Trail',
    description: 'A tamper-resistant, timestamped, searchable record of every operational event across every Kaiya module. One source of truth for auditors and regulators.',
    keywords: 'evidence ledger, audit trail, immutable records, compliance evidence, tamper-resistant logs',
  },
  '/operational-evidence': {
    title: 'Operational Evidence | Kaiya Solutions',
    description: 'Documented, verified, and defensible records of every business interaction, transaction, and compliance obligation.',
    keywords: 'operational evidence, business documentation, verification records',
  },
  '/industries': {
    title: 'Industries We Serve | Kaiya Solutions',
    description: 'Federal grant recipients, tribal governments, nonprofits, enterprises, contractors, importers and eCommerce brands — each with a purpose-built Kaiya module.',
    keywords: 'compliance software industries, federal grant recipients, tribal governments, contractor compliance, ecommerce compliance',
  },
  '/case-studies': {
    title: 'Case Studies | Kaiya Solutions',
    description: 'How governments, tribal councils, nonprofits, and enterprises use Kaiya to strengthen audit defensibility and close documentation gaps.',
    keywords: 'compliance case studies, audit preparation case studies, kaiya customer results',
  },
  '/about': {
    title: 'About Kaiya Solutions',
    description: 'Kaiya installs the operational infrastructure regulated organizations lack — documentation, verification, and defensible records across every compliance domain.',
    keywords: 'about kaiya solutions, compliance company, risk infrastructure company',
  },
  '/faq': {
    title: 'FAQ | Kaiya Solutions',
    description: 'Common questions about the Kaiya compliance platform, its AI, security, implementation timelines, and how each module fits your operations.',
    keywords: 'kaiya solutions faq, compliance software questions',
  },
  '/academy': {
    title: 'Certified Operators Academy | Kaiya Solutions',
    description: 'Kaiya\'s certification program for compliance consultants, implementers, and channel partners.',
    keywords: 'certified operators academy, compliance consultants, kaiya certification',
  },

  // ============================================================
  // Tools & Lead Magnets
  // ============================================================
  '/risk-assessment': {
    title: 'Request a Risk Assessment | Kaiya Solutions',
    description: 'Our team analyzes your operational risk surface and identifies where evidence, verification, and documentation infrastructure is missing.',
    keywords: 'risk assessment, compliance audit, operational risk consultation',
  },
  '/risk-scanner': {
    title: 'Risk Scanner | Free Compliance Diagnostic',
    description: 'Free diagnostic tool that surfaces the highest-priority operational risk gaps in your organization.',
    keywords: 'risk scanner, compliance diagnostic, free audit tool',
  },
  '/free-audit-tool': {
    title: 'Free Audit Readiness Tool | Kaiya Solutions',
    description: 'Free self-serve audit readiness assessment. Answer twelve questions and get an actionable evidence-gap report.',
    keywords: 'free audit tool, audit readiness assessment, compliance self-assessment',
  },

  // ============================================================
  // Contact / Conversion
  // ============================================================
  '/contact': {
    title: 'Request a Risk Assessment | Kaiya Solutions',
    description: 'Schedule a consultation to see how Kaiya\'s compliance and risk infrastructure — GrantGuard, VendorGuard, PROOF, CouncilGuard, ManifestGuard — fits your operations.',
    keywords: 'contact kaiya solutions, request risk assessment, compliance consultation',
  },
  '/contact-us': {
    title: 'Contact Kaiya Solutions',
    description: 'Get in touch with the Kaiya Solutions team about compliance, risk infrastructure, and platform capabilities.',
    keywords: 'contact kaiya solutions',
  },
  '/demo': {
    title: 'Book a Consultation | Kaiya Solutions',
    description: 'Book a live consultation to walk through the compliance modules relevant to your operations.',
    keywords: 'kaiya demo, compliance consultation',
  },

  // ============================================================
  // How-It-Works
  // ============================================================
  '/how-kaiya-works': {
    title: 'How the Kaiya Platform Works',
    description: 'How the modules connect: one authentication, one evidence ledger, one audit trail — powering every purpose-built compliance module.',
    keywords: 'how kaiya works, compliance platform architecture',
  },
  '/how-grantguard-works': {
    title: 'How GrantGuard Works | Federal Grant Compliance',
    description: 'Walk through GrantGuard: grant lifecycle, control mapping, gap detection, SEFA, subrecipient monitoring, and audit evidence.',
    keywords: 'how grantguard works, federal grant compliance workflow',
  },
  '/how-vendorguard-works': {
    title: 'How VendorGuard Works | Vendor Due Diligence',
    description: 'Walk through VendorGuard: vendor onboarding, COI collection, expiry tracking, risk scoring, and audit-ready vendor records.',
    keywords: 'how vendorguard works, vendor due diligence workflow',
  },
  '/how-proof-works': {
    title: 'How PROOF Works | Contractor Verification',
    description: 'Walk through PROOF: multi-step verification with photos, video, GPS, customer questionnaire, dual signatures, and shareable public links.',
    keywords: 'how proof works, contractor verification workflow',
  },
  '/how-manifestguard-works': {
    title: 'How ManifestGuard Works | Customs Compliance',
    description: 'Walk through ManifestGuard: pre-shipment document review, HS code classification, customs checklists, and release control.',
    keywords: 'how manifestguard works, customs compliance workflow',
  },
  '/how-ecom-protect-works': {
    title: 'How ECom Protect Works | Chargeback Defense',
    description: 'Walk through ECom Protect: Shopify webhook capture, evidence packaging, dispute representment, and recovery sequences.',
    keywords: 'how ecom protect works, chargeback defense workflow',
  },

  // ============================================================
  // Legal / Policy
  // ============================================================
  '/legal': {
    title: 'Legal | Kaiya Solutions',
    description: 'Terms of Service, Privacy Policy, Acceptable Use, Data Processing Addendum, Security Policy, and module-specific disclaimers.',
    keywords: 'kaiya legal, terms of service, privacy policy',
  },
  '/legal/terms': {
    title: 'Terms of Service | Kaiya Solutions',
    description: 'Kaiya Solutions Terms of Service covering platform use, AI-generated content, module-specific limitations, and dispute resolution.',
    keywords: 'terms of service, kaiya terms',
  },
  '/terms-of-service': {
    title: 'Terms of Service | Kaiya Solutions',
    description: 'Kaiya Solutions Terms of Service.',
    keywords: 'terms of service, kaiya terms',
  },
  '/legal/privacy': {
    title: 'Privacy Policy | Kaiya Solutions',
    description: 'How Kaiya Solutions collects, processes, and safeguards data across all platform modules, including AI-generated content processing.',
    keywords: 'privacy policy, data protection, GDPR, CCPA',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Kaiya Solutions',
    description: 'Kaiya Solutions Privacy Policy.',
    keywords: 'privacy policy',
  },
  '/privacy': {
    title: 'Privacy Policy | Kaiya Solutions',
    description: 'Kaiya Solutions Privacy Policy.',
    keywords: 'privacy policy',
  },
  '/legal/acceptable-use': {
    title: 'Acceptable Use Policy | Kaiya Solutions',
    description: 'Rules governing acceptable use of the Kaiya Solutions platform.',
    keywords: 'acceptable use policy',
  },
  '/acceptable-use': {
    title: 'Acceptable Use Policy | Kaiya Solutions',
    description: 'Rules governing acceptable use of the Kaiya Solutions platform.',
    keywords: 'acceptable use policy',
  },
  '/legal/proof-module-terms': {
    title: 'PROOF Module Terms | Kaiya Solutions',
    description: 'PROOF-specific terms of use governing job completion verification records and customer authorization.',
    keywords: 'PROOF terms',
  },
  '/legal/risk-disclosure': {
    title: 'Risk Disclosure | Risk Intelligence',
    description: 'Risk Intelligence content is provided for informational purposes and does not constitute legal, financial, or licensed audit advice.',
    keywords: 'risk disclosure, risk intelligence disclaimer',
  },
  '/ai-disclaimer': {
    title: 'AI Disclaimer | Kaiya Solutions',
    description: 'How Kaiya discloses AI-generated content and the requirement for human review before AI outputs become system-of-record entries.',
    keywords: 'AI disclaimer, AI disclosure, AI content policy',
  },
  '/councilguard/ai-disclosure': {
    title: 'CouncilGuard AI Disclosure',
    description: 'CouncilGuard AI disclosure covering meeting transcription, minutes generation, and decision extraction.',
    keywords: 'CouncilGuard AI disclosure',
  },
  '/councilguard/records-disclaimer': {
    title: 'CouncilGuard Records Disclaimer',
    description: 'Records disclaimer for CouncilGuard: AI-generated content requires human approval before public posting.',
    keywords: 'CouncilGuard records disclaimer',
  },
  '/councilguard/security': {
    title: 'CouncilGuard Security',
    description: 'Data security, encryption, and access controls for the CouncilGuard module.',
    keywords: 'CouncilGuard security',
  },
  '/councilguard/support': {
    title: 'CouncilGuard Support',
    description: 'Support resources, contact channels, and implementation help for CouncilGuard.',
    keywords: 'CouncilGuard support',
  },
  '/data-processing': {
    title: 'Data Processing Addendum | Kaiya Solutions',
    description: 'Enterprise Data Processing Addendum (DPA) for GDPR, CCPA, and enterprise customer data protection requirements.',
    keywords: 'data processing addendum, DPA, GDPR, CCPA',
  },
  '/email-compliance': {
    title: 'Email Compliance | Kaiya Solutions',
    description: 'Email sending, deliverability, and CAN-SPAM compliance for Kaiya-generated communications.',
    keywords: 'email compliance, CAN-SPAM',
  },
  '/refund-policy': {
    title: 'Refund Policy | Kaiya Solutions',
    description: 'Kaiya Solutions refund policy for subscription, per-use, and enterprise plans.',
    keywords: 'refund policy',
  },
  '/security': {
    title: 'Security | Kaiya Solutions',
    description: 'Encryption, access controls, multi-tenant isolation, and data protection posture across the Kaiya platform.',
    keywords: 'security, encryption, multi-tenant isolation, data protection',
  },
  '/security-policy': {
    title: 'Security Policy | Kaiya Solutions',
    description: 'Formal security policy governing the Kaiya Solutions platform.',
    keywords: 'security policy',
  },
  '/shopify-disclaimer': {
    title: 'Shopify Disclaimer | ECom Protect',
    description: 'ECom Protect Shopify integration disclaimer covering webhook data handling and merchant responsibility.',
    keywords: 'Shopify disclaimer, ECom Protect',
  },
};

/**
 * Resolve the metadata for a given pathname.
 * Falls back to defaultMetadata for unmapped routes.
 * Passing SEO props overrides the resolved entry (backward compat).
 */
export function resolveMetadata(pathname, overrides = {}) {
  const stripped = (pathname || '/').replace(/\/+$/, '') || '/';
  const entry = routeMetadata[stripped] || defaultMetadata;

  return {
    title: overrides.title || entry.title || defaultMetadata.title,
    description: overrides.description || entry.description || defaultMetadata.description,
    keywords: overrides.keywords || entry.keywords || defaultMetadata.keywords,
    image: overrides.image || entry.image || defaultMetadata.image,
    type: overrides.type || entry.type || defaultMetadata.type,
    canonical: overrides.canonical || entry.canonical || `${SITE_BASE_URL}${stripped}`,
    url: overrides.url || `${SITE_BASE_URL}${stripped}`,
  };
}
